import { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import {
  MdAdd,
  MdAssignment,
  MdClose,
  MdDeleteOutline,
  MdPerson,
  MdSettings,
  MdShoppingCart,
} from "react-icons/md";
import "./NewDeliveryNote.scss";

interface NewDeliveryNoteProps {
  onClose: () => void;
}

export default function NewDeliveryNote({ onClose }: NewDeliveryNoteProps) {
  const dialogRef = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;

    const previousOverflow = document.body.style.overflow;
    dialog.showModal();
    document.body.style.overflow = "hidden";

    return () => {
      dialog.close();
      document.body.style.overflow = previousOverflow;
    };
  }, []);

  return createPortal(
    <dialog
      ref={dialogRef}
      className="new-delivery-note-modal"
      aria-labelledby="new-delivery-note-title"
      aria-describedby="new-delivery-note-description"
      onCancel={(event) => {
        event.preventDefault();
        onClose();
      }}
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <form className="new-delivery-note-modal__form" onSubmit={(event) => event.preventDefault()}>
        <button
          type="button"
          className="new-delivery-note-modal__close"
          onClick={onClose}
          aria-label="Modal schließen"
          autoFocus
        >
          <MdClose aria-hidden="true" />
        </button>

        <header className="new-delivery-note-modal__header">
          <h2 id="new-delivery-note-title">Neuer Lieferschein</h2>
          <p id="new-delivery-note-description">
            Erfassen Sie den Empfänger und die ausgehenden Positionen. Pflichtfelder sind mit * markiert.
          </p>
        </header>

        <div className="new-delivery-note-modal__content">
          <div className="new-delivery-note-modal__top-grid">
            <section className="new-delivery-note-modal__section" aria-labelledby="delivery-recipient-title">
              <h2 id="delivery-recipient-title" className="new-delivery-note-modal__section-title">
                <MdPerson aria-hidden="true" /> Empfänger
              </h2>
              <div className="new-delivery-note-modal__section-content">
                <div className="new-delivery-note-modal__field">
                  <label htmlFor="delivery-customer-search">Kundensuche</label>
                  <input
                    id="delivery-customer-search"
                    name="customerSearch"
                    type="search"
                    placeholder="Kundensuche"
                    aria-describedby="delivery-customer-hint"
                  />
                </div>
                <div className="new-delivery-note-modal__recipient-actions">
                  <button
                    type="button"
                    className="new-delivery-note-modal__btn new-delivery-note-modal__btn--secondary"
                    title="Die Neukundenerstellung wird später angebunden"
                    disabled
                  >
                    <MdAdd aria-hidden="true" /> Neukunde
                  </button>
                </div>
                <div className="new-delivery-note-modal__field-row">
                  <div className="new-delivery-note-modal__field">
                    <label htmlFor="delivery-branch">oder Filiale</label>
                    <select id="delivery-branch" name="branch" defaultValue="" disabled>
                      <option value="">Noch keine Filialen verfügbar</option>
                    </select>
                  </div>
                  <button
                    type="button"
                    className="new-delivery-note-modal__btn new-delivery-note-modal__btn--secondary"
                    title="Die Empfängerauswahl wird später angebunden"
                    disabled
                  >
                    auswählen
                  </button>
                </div>
                <p id="delivery-customer-hint" className="new-delivery-note-modal__hint">
                  Die Empfängerverwaltung wird später angebunden.
                </p>
              </div>
            </section>

            <section className="new-delivery-note-modal__section" aria-labelledby="delivery-address-title">
              <h2 id="delivery-address-title" className="new-delivery-note-modal__section-title">
                Alternative Lieferanschrift
              </h2>
              <div className="new-delivery-note-modal__section-content">
                <p className="new-delivery-note-modal__hint">
                  Optional abweichende Lieferanschrift erfassen.
                </p>
              </div>
            </section>
          </div>

          <section className="new-delivery-note-modal__section" aria-labelledby="delivery-articles-title">
            <h2 id="delivery-articles-title" className="new-delivery-note-modal__section-title">
              <MdShoppingCart aria-hidden="true" /> Artikel hinzufügen
            </h2>
            <div className="new-delivery-note-modal__section-content">
              <div className="new-delivery-note-modal__field-row">
                <div className="new-delivery-note-modal__field">
                  <label htmlFor="delivery-article-search">Artikelsuche</label>
                  <input
                    id="delivery-article-search"
                    name="articleSearch"
                    type="search"
                    placeholder="Artikelname oder Artikelnummer"
                    aria-describedby="delivery-article-hint"
                  />
                </div>
                <div className="new-delivery-note-modal__field">
                  <label htmlFor="delivery-imei">IMEI</label>
                  <input
                    id="delivery-imei"
                    name="imei"
                    type="text"
                    inputMode="numeric"
                    placeholder="IMEI eingeben oder scannen"
                    aria-describedby="delivery-article-hint"
                  />
                </div>
                <div className="new-delivery-note-modal__field">
                  <label htmlFor="delivery-ean">EAN</label>
                  <input
                    id="delivery-ean"
                    name="ean"
                    type="text"
                    inputMode="numeric"
                    placeholder="EAN eingeben oder scannen"
                    aria-describedby="delivery-article-hint"
                  />
                </div>
              </div>
              <p id="delivery-article-hint" className="new-delivery-note-modal__hint">
                Die Artikelsuche wird später mit den Stammdaten verbunden.
              </p>
            </div>
          </section>

          <section className="new-delivery-note-modal__section" aria-labelledby="delivery-items-title">
            <h2 id="delivery-items-title" className="new-delivery-note-modal__section-title">
              <MdAssignment aria-hidden="true" /> Positionen auf Lieferschein
            </h2>
            <div className="new-delivery-note-modal__table-wrapper" role="region" aria-labelledby="delivery-items-title" tabIndex={0}>
              <table className="new-delivery-note-modal__table" aria-labelledby="delivery-items-title">
                <thead>
                  <tr>
                    <th scope="col"><input type="checkbox" aria-label="Alle Positionen auswählen" disabled /></th>
                    <th scope="col">Nr.</th>
                    <th scope="col">Beschreibung</th>
                    <th scope="col">Bemerkung</th>
                    <th scope="col">IMEI</th>
                    <th scope="col">Menge</th>
                    <th scope="col">EK</th>
                    <th scope="col">VK</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td colSpan={8} className="new-delivery-note-modal__empty-state">
                      Noch keine Artikel zum Lieferschein hinzugefügt.
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          <section className="new-delivery-note-modal__section" aria-labelledby="delivery-actions-title">
            <h2 id="delivery-actions-title" className="new-delivery-note-modal__section-title">
              <MdSettings aria-hidden="true" /> Aktion
            </h2>
            <div className="new-delivery-note-modal__action-content">
              <div className="new-delivery-note-modal__field">
                <label htmlFor="delivery-notes">Notizen</label>
                <textarea id="delivery-notes" name="notes" rows={3} placeholder="Notizen zum Lieferschein" />
              </div>
              <div className="new-delivery-note-modal__footer-actions">
                <button
                  type="button"
                  className="new-delivery-note-modal__btn new-delivery-note-modal__btn--secondary"
                  title="Keine Positionen zum Löschen vorhanden"
                  disabled
                >
                  <MdDeleteOutline aria-hidden="true" /> Löschen
                </button>
                <p id="delivery-booking-hint" className="new-delivery-note-modal__hint">
                  Die Buchung wird später angebunden.
                </p>
                <button
                  type="submit"
                  className="new-delivery-note-modal__btn new-delivery-note-modal__btn--primary"
                  aria-describedby="delivery-booking-hint"
                  disabled
                >
                  Warenausgang buchen und Lieferschein erstellen
                </button>
              </div>
            </div>
          </section>
        </div>
      </form>
    </dialog>,
    document.body,
  );
}
